import { toastError, toastSuccess } from "@hubrise/react-components"
import { noop } from "lodash"
import * as React from "react"
import { useTranslation } from "react-i18next"

import ConflictMessage from "shared/components/EditorStoreContext/ConflictMessage"
import useBeforeUnload from "shared/hooks/useBeforeUnload"
import usePageTitle from "shared/hooks/usePageTitle"
import { Editor } from "shared/hubrise/editor"

import { computeEditorsHash } from "./utils"

type TEditorDictionary = { [catalogId: string]: Editor }

const replaceState = (state: EditorStoreContextState, editor: Editor): EditorStoreContextState => {
  const catalogHrId = editor.catalog.hrId
  const openEditors = { ...state.openEditors, [catalogHrId]: editor }
  const editorsHash = computeEditorsHash(openEditors)
  return { openEditors, editorsHash }
}

type EditorStoreContextState = {
  openEditors: TEditorDictionary
  editorsHash: string
}

type EditorStoreContextInterface = {
  /** The list of open editors */
  openEditors: TEditorDictionary
  /** Add or replace an editor in the store */
  setEditor: (editor: Editor, afterChange?: () => void) => void
  /** Must be called when a catalog is updated, to recompute the hash. */
  onChange: () => void
  /** Causes the consumer of the context to re-render when a catalog is updated. */
  editorsHash: string
  /** Indicates whether the store has unsaved changes. */
  hasUnsavedChanges: boolean
}

const EditorStoreContext = React.createContext<EditorStoreContextInterface | undefined>(undefined)

export const EditorStoreContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { t } = useTranslation()

  const [state, setState] = React.useState<EditorStoreContextState>({ openEditors: {}, editorsHash: "" })
  const { openEditors, editorsHash } = state

  const onChange = React.useCallback((): void => {
    setState(({ openEditors }) => ({ openEditors, editorsHash: computeEditorsHash(openEditors) }))
  }, [])

  const setEditor = React.useCallback<EditorStoreContextInterface["setEditor"]>(
    (editor, afterChange = noop) => {
      setState((state): EditorStoreContextState => {
        const catalogHrId = editor.catalog.hrId
        const catalogName = editor.catalog.name
        const currentEditor: Editor | undefined = state.openEditors[catalogHrId]
        const differentCatalogs = editor.catalogHash !== currentEditor?.catalogHash

        const discard = () => state
        const save = () => replaceState(state, editor)

        const forceSave = () => {
          setState((state) => replaceState(state, editor))
          afterChange()
          // Wait to let the error toast disappear
          setTimeout(() => toastSuccess(t("catalog.server_reload.conflict.success", { catalogName })), 500)
        }

        if (currentEditor === undefined) {
          return save()
        } else if (differentCatalogs) {
          // Not a conflict if the current unsaved editor is based on the same server catalog.
          const sameServerCatalog = editor.catalogHash === currentEditor.catalogServerHash
          if (sameServerCatalog) return discard()

          if (currentEditor.unsaved) {
            toastError(
              t("catalog.server_reload.conflict.title", { catalogName }),
              <ConflictMessage forceSave={forceSave} />,
            )
            return discard()
          } else {
            toastSuccess(t("catalog.server_reload.success", { catalogName }))
            return save()
          }
        } else {
          return discard()
        }
      })
      afterChange()
    },
    [t],
  )

  const hasUnsavedChanges = Object.values(openEditors).some((editor) => editor.unsaved)
  useBeforeUnload(hasUnsavedChanges)
  usePageTitle(hasUnsavedChanges)

  const value = React.useMemo(
    () => ({ openEditors, setEditor, onChange, editorsHash, hasUnsavedChanges }),
    [openEditors, setEditor, onChange, editorsHash, hasUnsavedChanges],
  )

  return <EditorStoreContext.Provider value={value}>{children}</EditorStoreContext.Provider>
}

export function useEditorStoreContext(): EditorStoreContextInterface {
  const context = React.useContext(EditorStoreContext)

  if (context === undefined) {
    throw new Error(`useEditorStoreContext must be used within EditorStoreContextProvider`)
  }

  return context
}
